/**
 * Componente de Mapa Geoespacial (MapComponent).
 * Proporciona una visualización interactiva de datos epidemiológicos y climáticos
 * utilizando Leaflet. Soporta capas de calor, coropletas y marcadores personalizados.
 */

import React, { useEffect, useState, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, LayersControl, LayerGroup, GeoJSON, CircleMarker, Tooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import 'leaflet.heat';
import axios from 'axios';
import { Apiurl } from "../../shared/services/apirest";

// --- CONFIGURACIÓN DE ICONOS DE LEAFLET (COMPATIBILIDAD CON WEBPACK) ---
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconUrl: markerIcon,
    iconRetinaUrl: markerIcon2x,
    shadowUrl: markerShadow,
});

// --- CONSTANTES Y UTILIDADES DE ICONOS ---
const CENTER = [8.75, -75.88]; // Centro del Departamento de Córdoba

/**
 * Crea un icono personalizado en forma de parásito con color dinámico.
 * @param {string} color - Color hexadecimal o nombre de color estándar.
 */
const createParasiteIcon = (color) => {
    // Mapeo de colores para riesgo
    const colorMap = {
        'red': '#EF5350',   // Riesgo Alto
        'orange': '#FFA726', // Riesgo Medio
        'green': '#66BB6A', // Riesgo Bajo
        'blue': '#42A5F5',
        'purple': '#AB47BC',
        'teal': '#26A69A',
        'brown': '#8D6E63'
    };

    const hexColor = colorMap[color] || color;

    // SVG que representa un parásito (gusano) de forma estilizada
    return L.divIcon({
        className: 'custom-parasite-icon',
        html: `
        <div style="width: 40px; height: 40px; filter: drop-shadow(0px 2px 2px rgba(0,0,0,0.5)); transition: transform 0.2s;">
            <svg viewBox="0 0 100 60" width="40" height="24" xmlns="http://www.w3.org/2000/svg" style="overflow: visible;">
                <!-- Cuerpo (Contorno) -->
                <path d="M10,30 Q25,5 40,30 T70,30 T90,10" fill="none" stroke="white" stroke-width="14" stroke-linecap="round" />
                <!-- Cuerpo (Color) -->
                <path d="M10,30 Q25,5 40,30 T70,30 T90,10" fill="none" stroke="${hexColor}" stroke-width="10" stroke-linecap="round" />
                <!-- Cabeza -->
                <circle cx="10" cy="30" r="14" fill="${hexColor}" stroke="white" stroke-width="2" />
                <!-- Ojos -->
                <circle cx="6" cy="26" r="3" fill="white" />
                <circle cx="14" cy="26" r="3" fill="white" />
                <circle cx="6" cy="26" r="1" fill="black" />
                <circle cx="14" cy="26" r="1" fill="black" />
            </svg>
        </div>`,
        iconSize: [40, 40],
        iconAnchor: [20, 20],
        popupAnchor: [0, -20]
    });
};

const ICONS = {
    ALTO: createParasiteIcon('red'),
    MEDIO: createParasiteIcon('orange'),
    BAJO: createParasiteIcon('green'),
    DEFAULT: createParasiteIcon('blue')
};

// --- SUBCOMPONENTES ---

/**
 * Capa de Mapa de Calor.
 * Renderiza una capa de densidad basada en puntos geográficos.
 */
const HeatmapLayer = ({ points, layerGroup }) => {
    useEffect(() => {
        if (!points || points.length === 0 || !L.heatLayer || !layerGroup) return;

        const heat = L.heatLayer(points, {
            radius: 40,
            blur: 25,
            maxZoom: 10,
            max: 1.0,
            minOpacity: 0.4,
            gradient: { 0.2: 'blue', 0.4: 'lime', 0.6: 'yellow', 0.8: 'orange', 1.0: 'red' }
        });

        // Añadir a LayerGroup para control de capas, no al mapa directamente
        heat.addTo(layerGroup);

        return () => {
            try { layerGroup.removeLayer(heat); } catch (e) { }
        };
    }, [points, layerGroup]);
    return null;
};

// --- COMPONENTE PRINCIPAL ---

const MapComponent = ({ data, activeVariable }) => {
    // Estados para datos epidemiológicos y capas
    const [casos, setCasos] = useState([]);
    const [alerts, setAlerts] = useState([]);
    const [geoJsonCordoba, setGeoJsonCordoba] = useState(null);
    const [geoJsonMunicipios, setGeoJsonMunicipios] = useState(null);
    const [climateGeoJson, setClimateGeoJson] = useState(null);
    const [geoJsonError, setGeoJsonError] = useState(false);
    const [heatmapGroup, setHeatmapGroup] = useState(null);

    // Estados para clima dinámico (Choropleth)
    const [weatherData, setWeatherData] = useState(null);
    const [weatherCache, setWeatherCache] = useState({});
    const [loadingWeather, setLoadingWeather] = useState(false);

    // Coordenadas fijas de municipios de Córdoba (como respaldo ante fallos de GeoJSON)
    const cordobaMunsCoords = useMemo(() => ({
        "Montería": [8.74798, -75.88143],
        "Ayapel": [8.31371, -75.13953],
        "Buenavista": [8.6853, -75.2576],
        "Canalete": [8.7903, -76.2443],
        "Cereté": [8.8837, -75.7933],
        "Chimá": [9.1492, -75.7275],
        "Chinú": [9.1084, -75.4027],
        "Ciénaga de Oro": [8.8753, -75.6214],
        "Cotorra": [9.0383, -75.7892],
        "La Apartada": [8.1065, -75.3855],
        "Lorica": [9.2319, -75.8142],
        "Los Córdobas": [8.8953, -76.3533],
        "Momil": [9.2344, -75.6450],
        "Montelíbano": [7.9814, -75.4194],
        "Moñitos": [9.2458, -76.1287],
        "Planeta Rica": [8.4111, -75.5878],
        "Pueblo Nuevo": [8.5022, -75.5208],
        "Puerto Escondido": [8.9567, -76.2650],
        "Puerto Libertador": [7.8872, -75.6715],
        "Purísima": [9.2389, -75.7225],
        "Sahagún": [8.9472, -75.4433],
        "San Andrés de Sotavento": [9.1469, -75.5108],
        "San Antero": [9.3739, -75.7583],
        "San Bernardo del Viento": [9.3542, -75.9525],
        "San Carlos": [8.7981, -75.7003],
        "San José de Uré": [7.7818, -75.5346],
        "San Pelayo": [8.9583, -75.8500],
        "Tierralta": [8.1731, -76.0592],
        "Tuchín": [9.1834, -75.5562],
        "Valencia": [8.2718, -76.1554]
    }), []);

    // 1. Obtención de capas GeoJSON al montar el componente con mecanismo de reintentos
    useEffect(() => {
        const fetchGeoJSON = async () => {
            let foundValid = false; // Rastrea si se encontró algún GeoJSON válido

            // Primero: Intentar obtener el límite departamental (Córdoba)
            try {
                const deptUrls = [
                    'https://raw.githubusercontent.com/finiterank/mapa-colombia-js/master/colombia.json',
                    'https://raw.githubusercontent.com/da-mendez/colombia-geojson/master/colombia.json'
                ];
                for (const url of deptUrls) {
                    try {
                        const res = await axios.get(url);
                        const cordoba = res.data.features?.find(f =>
                            f.properties.DPTO === "23" ||
                            (f.properties.NOMBRE_DPT && f.properties.NOMBRE_DPT.toUpperCase() === "CORDOBA") ||
                            (f.properties.dpt && f.properties.dpt.toUpperCase() === "CORDOBA")
                        );
                        if (cordoba) {
                            setGeoJsonCordoba(cordoba);
                            break;
                        }
                    } catch (e) { continue; }
                }
            } catch (e) { console.error("Error al obtener GeoJSON del Departamento", e); }

            // Segundo: Obtener límites Municipales con múltiples fuentes de respaldo
            const munUrls = [
                'https://raw.githubusercontent.com/da-mendez/colombia-geojson/master/municipios.json',
                'https://raw.githubusercontent.com/feliperuizruiz/mapa-colombia/master/municipios.json',
                'https://raw.githubusercontent.com/finiterank/mapa-colombia-js/master/colombia-municipios.json',
                'https://raw.githubusercontent.com/john-guerra/727e8992e9599b9d9f1dbfdc4c8e479e/raw/colombia-municipios.json'
            ];

            for (const url of munUrls) {
                try {
                    const res = await axios.get(url);
                    if (!res.data || res.data.type !== "FeatureCollection") continue;

                    const cordobaMuns = res.data.features.filter(f => {
                        const p = f.properties;
                        return p.DPTO === "23" || p.COD_DEPTO === "23" ||
                            (p.NOMBRE_DPT && p.NOMBRE_DPT.toUpperCase() === "CORDOBA") ||
                            (p.dpt && p.dpt.toUpperCase() === "CORDOBA") ||
                            (p.DPTO_CCD === "23"); // Soporta propiedades oficiales IGAC
                    });

                    if (cordobaMuns.length > 0) {
                        // Estandarizar nombres de propiedades para uso posterior
                        const standardizedFeatures = cordobaMuns.map(f => ({
                            ...f,
                            properties: {
                                ...f.properties,
                                name: f.properties.NOMBRE_MPI || f.properties.name || f.properties.NOM_MUN || f.properties.MPIO_CNMBR || f.properties.NOM_MUNICIPIO
                            }
                        }));
                        const featureCollection = { type: "FeatureCollection", features: standardizedFeatures };
                        setGeoJsonMunicipios(featureCollection);
                        setClimateGeoJson(featureCollection);
                        foundValid = true;
                        break;
                    }
                } catch (error) {
                    console.error(`Error al obtener GeoJSON desde ${url}:`, error.message);
                }
            }

            if (!foundValid) {
                setGeoJsonError(true);
            }
        };
        fetchGeoJSON();
    }, []);

    // Also log whenever activeVariable or weatherData changes
    useEffect(() => {
        console.log("MapComponent: activeVariable changed to:", activeVariable);
    }, [activeVariable]);

    useEffect(() => {
        console.log("MapComponent: weatherData updated:", weatherData ? Object.keys(weatherData).length + " municipalities" : "null");
    }, [weatherData]);

    // 2. Procesamiento y carga de Datos Epidemiológicos
    useEffect(() => {
        const token = localStorage.getItem('token');
        const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

        /**
         * Transforma los datos brutos del servidor al formato del mapa.
         */
        const processCases = (rawData) => {
            if (!Array.isArray(rawData)) return [];
            return rawData.map(c => {
                let p = c.parasitos_detectados;
                if (!p) p = [];
                else if (typeof p === 'string') {
                    try {
                        if (p.startsWith('[') && p.endsWith(']')) p = JSON.parse(p.replace(/'/g, '"'));
                        else p = [p];
                    } catch (e) { p = [p]; }
                }
                return {
                    id: c.id,
                    lat: parseFloat(c.latitud),
                    lon: parseFloat(c.longitud),
                    nivel: (c.nivel_peligrosidad || "BAJO").toUpperCase(),
                    score: c.algoritmo_detail?.final_score || 0,
                    parasitos: Array.isArray(p) ? p : [],
                    municipio: c.paciente_municipio || "Sin Definir",
                    fecha: c.fecha_registro
                };
            }).filter(c => !isNaN(c.lat) && !isNaN(c.lon));
        };

        if (data) {
            setCasos(processCases(data));
        } else {
            axios.get(`${Apiurl}casos/`, config).then(res => setCasos(processCases(res.data))).catch(console.error);
        }

        // Obtener Alertas activas para el motor de alertas
        axios.get(`${Apiurl}casos/alertas/`, config)
            .then(res => setAlerts(res.data.filter(a => !a.is_read)))
            .catch(console.error);

    }, [data]);

    // 3. Lógica de Consulta y Caché de Clima Dinámico
    useEffect(() => {
        if (!activeVariable) return;

        const CACHE_STALENESS_LIMIT = 3600 * 1000; // 1 hora
        const now = Date.now();
        const cached = weatherCache[activeVariable];

        if (cached && (now - cached.timestamp < CACHE_STALENESS_LIMIT)) {
            setWeatherData(cached.data);
            return;
        }

        /**
         * Consulta Open-Meteo para obtener datos en tiempo real por cada municipio.
         */
        const fetchWeather = async () => {
            setLoadingWeather(true);
            try {
                let coords = [];
                if (geoJsonMunicipios) {
                    // Obtener centroides del GeoJSON municipal
                    const muns = geoJsonMunicipios.features;
                    coords = muns.map(f => {
                        const name = f.properties.name;
                        let poly = f.geometry.coordinates[0];
                        if (f.geometry.type === "MultiPolygon") poly = f.geometry.coordinates[0][0];

                        const lat = poly[0][1];
                        const lon = poly[0][0];

                        return { lat, lon, name };
                    });
                } else if (geoJsonError) {
                    // Respaldo con coordenadas fijas
                    coords = Object.entries(cordobaMunsCoords).map(([name, [lat, lon]]) => ({ lat, lon, name }));
                } else {
                    setLoadingWeather(false);
                    return;
                }

                if (coords.length === 0) {
                    setLoadingWeather(false);
                    return;
                }

                const lats = coords.map(c => c.lat).join(',');
                const lons = coords.map(c => c.lon).join(',');

                let params = "";
                if (activeVariable === 'temperatura') params = "current=temperature_2m";
                else if (activeVariable === 'humedad') params = "current=relative_humidity_2m";
                else if (activeVariable === 'precipitacion') params = "current=precipitation";
                else if (activeVariable === 'uv') params = "current=uv_index";

                const response = await axios.get(`https://api.open-meteo.com/v1/forecast?latitude=${lats}&longitude=${lons}&${params}&timezone=auto`);

                const results = {};
                const responseDataArray = Array.isArray(response.data) ? response.data : [response.data];

                coords.forEach((c, idx) => {
                    const locationData = responseDataArray[idx];
                    const current = locationData?.current;
                    let val = null;
                    if (current) {
                        if (activeVariable === 'temperatura') val = current.temperature_2m;
                        else if (activeVariable === 'humedad') val = current.relative_humidity_2m;
                        else if (activeVariable === 'precipitacion') val = current.precipitation;
                        else if (activeVariable === 'uv') val = current.uv_index;
                    }
                    results[c.name] = val;
                });

                setWeatherData(results);
                setWeatherCache(prev => ({
                    ...prev,
                    [activeVariable]: { data: results, timestamp: now }
                }));
            } catch (error) {
                console.error("Error al obtener clima para coropletas:", error);
            } finally {
                setLoadingWeather(false);
            }
        };

        fetchWeather();
    }, [activeVariable, geoJsonMunicipios, geoJsonError, cordobaMunsCoords]);

    // 4. Lógica de Escalas de Color y Tooltips del Clima
    const getColor = (value, variable) => {
        if (variable === 'temperatura') {
            return value > 35 ? '#800026' : value > 30 ? '#BD0026' : value > 25 ? '#E31A1C' :
                value > 20 ? '#FC4E2A' : value > 15 ? '#FD8D3C' : '#FEB24C';
        }
        if (variable === 'humedad') {
            return value > 80 ? '#084594' : value > 60 ? '#2171b5' : value > 40 ? '#4292c6' : '#9ecae1';
        }
        if (variable === 'precipitacion') {
            return value > 30 ? '#08306b' : value > 5 ? '#2171b5' : value > 0.1 ? '#6baed6' : '#eff3ff';
        }
        if (variable === 'uv') {
            return value >= 11 ? '#6d4c41' : value >= 8 ? '#d32f2f' : value >= 6 ? '#f57c00' :
                value >= 3 ? '#fbc02d' : '#388e3c';
        }
        return 'gray';
    };

    const getUnit = (variable) => {
        if (variable === 'temperatura') return '°C';
        if (variable === 'humedad') return '%';
        if (variable === 'precipitacion') return 'mm';
        if (variable === 'uv') return '';
        return '';
    };

    const climateStyle = (feature) => {
        const name = feature.properties.name;
        const val = weatherData ? weatherData[name] : null;
        return {
            fillColor: (val !== null && val !== undefined) ? getColor(val, activeVariable) : '#cccccc',
            weight: 1.5,
            opacity: 1,
            color: 'white',
            dashArray: '3',
            fillOpacity: 0.8
        };
    };

    const onEachClimateFeature = (feature, layer) => {
        const name = feature.properties.name;
        const val = weatherData ? weatherData[name] : null;
        const unit = getUnit(activeVariable);
        const labelMap = { temperatura: 'Temperatura', humedad: 'Humedad', precipitacion: 'Precipitación', uv: 'Índice UV' };
        const label = labelMap[activeVariable] || 'Variable';

        layer.bindTooltip(`
            <div style="text-align: center; font-family: sans-serif;">
                <strong style="color: #333;">${name}</strong><br/>
                <span style="font-size: 1.1em; color: ${val !== null ? getColor(val, activeVariable) : '#666'}">
                    ${label}: ${val !== null ? val + unit : 'Cargando...'}
                </span>
            </div>
        `, { sticky: true, opacity: 0.9 });
    };

    // 5. Agregación de Datos por Municipio para Marcadores e Indicadores
    const municipalAggregates = useMemo(() => {
        const stats = {};
        casos.forEach(c => {
            const m = c.municipio;
            if (!stats[m]) {
                stats[m] = {
                    municipio: m,
                    total: 0,
                    risks: { ALTO: 0, MEDIO: 0, BAJO: 0 },
                    parasites: new Set(),
                    lat: c.lat,
                    lon: c.lon,
                    dates: [],
                    activeCount: 0
                };
            }
            stats[m].total++;
            stats[m].risks[c.nivel] = (stats[m].risks[c.nivel] || 0) + 1;
            c.parasitos.forEach(p => stats[m].parasites.add(p));
            if (c.fecha) stats[m].dates.push(new Date(c.fecha));

            const d = new Date(c.fecha);
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
            if (d >= thirtyDaysAgo) stats[m].activeCount++;
        });

        return Object.values(stats).map(item => {
            let pred = "BAJO";
            let max = item.risks.BAJO;
            if (item.risks.MEDIO > max) { max = item.risks.MEDIO; pred = "MEDIO"; }
            if (item.risks.ALTO > max) { max = item.risks.ALTO; pred = "ALTO"; }

            const lastDate = item.dates.length > 0 ? new Date(Math.max(...item.dates)) : new Date();

            return {
                ...item,
                predominantRisk: pred,
                parasitesList: Array.from(item.parasites),
                lastUpdate: lastDate.toLocaleDateString(),
                trend: item.activeCount > 0 ? "Incremento reciente" : "Estable"
            };
        });
    }, [casos]);

    const heatPoints = useMemo(() => {
        return municipalAggregates.map(m => [m.lat, m.lon, Math.min(m.total / 10 + 0.3, 1.0)]);
    }, [municipalAggregates]);

    /**
     * Popup detallado para un municipio.
     */
    const MunicipalPopup = ({ item }) => (
        <Popup>
            <div style={{ minWidth: '200px' }}>
                <div className="border-bottom mb-2 pb-1">
                    <h6 className="mb-0 fw-bold text-primary">📍 Municipio: {item.municipio}</h6>
                    <small className="text-muted">🗂️ Zona: Córdoba</small>
                </div>
                <div className="mb-2">
                    <ul className="list-unstyled mb-0 small">
                        <li>• Casos acumulados: <strong>{item.total}</strong></li>
                        <li>• Nivel de riesgo: <span className={`badge ${item.predominantRisk === 'ALTO' ? 'bg-danger' : item.predominantRisk === 'MEDIO' ? 'bg-warning text-dark' : 'bg-success'}`}>{item.predominantRisk}</span></li>
                        <li>• Parásitos detectados:
                            <ul className="ps-3 mb-0">
                                {item.parasitesList.length > 0 ? item.parasitesList.map(p => <li key={p}>{p}</li>) : <li>Ninguno detectado</li>}
                            </ul>
                        </li>
                    </ul>
                </div>
                <div className="border-top pt-2 mb-1">
                    <div className="small">📈 <strong>Tendencia:</strong> {item.trend}</div>
                </div>
                <div className="text-muted" style={{ fontSize: '0.65rem' }}>
                    🕒 Última actualización: {item.lastUpdate}
                </div>
            </div>
        </Popup>
    );

    return (
        <div style={{ position: 'relative', height: '100%', width: '100%' }}>
            <MapContainer center={CENTER} zoom={9} style={{ height: "100%", width: "100%" }}>
                <LayersControl position="topright" collapsed={false}>
                    {/* 1. Capas Base de Mapa */}
                    <LayersControl.BaseLayer checked name="Mapa General">
                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
                    </LayersControl.BaseLayer>
                    <LayersControl.BaseLayer name="Satélite">
                        <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" attribution='&copy; Esri' />
                    </LayersControl.BaseLayer>

                    {/* 2. Superposiciones de Límites Administrativos */}
                    <LayersControl.Overlay checked name="🏛️ Límite Departamental">
                        <LayerGroup>
                            {geoJsonCordoba && <GeoJSON data={geoJsonCordoba} style={{ color: '#6B8E23', weight: 4, fillOpacity: 0.05 }} />}
                        </LayerGroup>
                    </LayersControl.Overlay>

                    {/* 3. CAPA DINÁMICA DE CLIMA (Choropleth/Puntos) */}
                    <LayersControl.Overlay
                        checked={!!activeVariable}
                        name={activeVariable ? `🌡️ Clima: ${activeVariable.charAt(0).toUpperCase() + activeVariable.slice(1)}` : "🌡️ Clima (Seleccione variable)"}
                    >
                        {activeVariable ? (
                            <LayerGroup>
                                {climateGeoJson ? (
                                    <GeoJSON
                                        key={`geojson-${activeVariable}-${!!weatherData}`}
                                        data={climateGeoJson}
                                        style={climateStyle}
                                        onEachFeature={onEachClimateFeature}
                                    />
                                ) : (
                                    // Respaldo: Renderizar CircleMarkers si el GeoJSON falla
                                    Object.entries(cordobaMunsCoords).map(([munName, coords]) => {
                                        const val = weatherData ? weatherData[munName] : null;
                                        return (
                                            <CircleMarker
                                                key={`fallback-marker-${munName}-${activeVariable}`}
                                                center={coords}
                                                radius={val !== null ? 15 : 8}
                                                pathOptions={{
                                                    fillColor: (val !== null && val !== undefined) ? getColor(val, activeVariable) : '#cccccc',
                                                    fillOpacity: 0.8,
                                                    color: '#ffffff',
                                                    weight: 1
                                                }}
                                            >
                                                <Tooltip sticky>
                                                    <div className="text-center">
                                                        <strong className="text-primary">{munName}</strong><br />
                                                        <span className="fw-bold" style={{ color: (val !== null && val !== undefined) ? getColor(val, activeVariable) : '#666' }}>
                                                            {activeVariable.charAt(0).toUpperCase() + activeVariable.slice(1)}: {val !== null ? val + getUnit(activeVariable) : 'Sin datos'}
                                                        </span>
                                                    </div>
                                                </Tooltip>
                                            </CircleMarker>
                                        );
                                    })
                                )}
                            </LayerGroup>
                        ) : <LayerGroup />}
                    </LayersControl.Overlay>

                    <LayersControl.Overlay checked={!activeVariable} name="🏘️ Grilla Municipal">
                        <LayerGroup>
                            {geoJsonMunicipios && (
                                <GeoJSON
                                    data={geoJsonMunicipios}
                                    style={{ color: '#555', weight: 1, dashArray: '5, 5', fillOpacity: 0 }}
                                    onEachFeature={(feature, layer) => {
                                        layer.bindTooltip(feature.properties.NOMBRE_MPI || "Municipio", { sticky: true });
                                    }}
                                />
                            )}
                        </LayerGroup>
                    </LayersControl.Overlay>

                    {/* 4. Capas de Riesgo Epidemiológico */}
                    {['ALTO', 'MEDIO', 'BAJO'].map(riskLevel => (
                        <LayersControl.Overlay key={riskLevel} checked name={`${riskLevel === 'ALTO' ? '🔴' : riskLevel === 'MEDIO' ? '🟠' : '🟢'} Casos - Riesgo ${riskLevel.charAt(0) + riskLevel.slice(1).toLowerCase()}`}>
                            <LayerGroup>
                                {municipalAggregates
                                    .filter(item => item.risks[riskLevel] > 0)
                                    .map((item, idx) => (
                                        <Marker key={`${riskLevel}-${idx}`} position={[item.lat, item.lon]} icon={ICONS[riskLevel]}>
                                            <Tooltip sticky>
                                                <div className="text-center">
                                                    <strong>Municipio: {item.municipio}</strong><br />
                                                    Riesgo: {riskLevel}<br />
                                                    Casos reportados: {item.risks[riskLevel]}
                                                </div>
                                            </Tooltip>
                                            <MunicipalPopup item={item} />
                                        </Marker>
                                    ))}
                            </LayerGroup>
                        </LayersControl.Overlay>
                    ))}

                    {/* 5. Capas por Tipo de Parásito */}
                    {[
                        { name: '<i>Ascaris lumbricoides</i>', key: 'Ascaris', color: 'purple', emoji: '🟣' },
                        { name: '<i>Trichuris trichiura</i>', key: 'Trichuris', color: 'teal', emoji: '🔵' },
                        { name: 'Uncinarias', key: 'Uncinarias', color: 'brown', emoji: '🟤' }
                    ].map(p => (
                        <LayersControl.Overlay key={p.key} name={`${p.emoji} ${p.name}`}>
                            <LayerGroup>
                                {municipalAggregates
                                    .filter(item => item.parasites.has(p.key))
                                    .map((item, idx) => (
                                        <CircleMarker key={`${p.key}-${idx}`} center={[item.lat, item.lon]} radius={10} pathOptions={{ color: p.color, fillColor: p.color, fillOpacity: 0.7 }}>
                                            <Tooltip sticky>
                                                <div className="text-center">
                                                    <strong>Municipio: {item.municipio}</strong><br />
                                                    Parásito: {p.name}<br />
                                                    Presencia: Confirmada
                                                </div>
                                            </Tooltip>
                                            <MunicipalPopup item={item} />
                                        </CircleMarker>
                                    ))}
                            </LayerGroup>
                        </LayersControl.Overlay>
                    ))}

                    {/* 6. Capa de Densidad (Mapa de Calor) */}
                    <LayersControl.Overlay checked={false} name="Mapa de Calor - Densidad">
                        <LayerGroup ref={setHeatmapGroup}>
                            <HeatmapLayer points={heatPoints} layerGroup={heatmapGroup} />
                            {municipalAggregates.map((item, idx) => (
                                <CircleMarker key={'heat-' + idx} center={[item.lat, item.lon]} radius={20} pathOptions={{ opacity: 0, fillOpacity: 0 }}>
                                    <Tooltip sticky>
                                        <div className="text-center">
                                            <strong>Zona de concentración elevada</strong><br />
                                            Casos acumulados: {item.total}
                                        </div>
                                    </Tooltip>
                                    <Popup>
                                        <div className="text-center">
                                            <h6 className="fw-bold mb-1">Zona con alta concentración de casos</h6>
                                            <p className="small mb-1">Periodo analizado: Últimos 90 días</p>
                                            <span className="badge bg-warning text-dark">Nivel de alerta: Moderado</span>
                                        </div>
                                    </Popup>
                                </CircleMarker>
                            ))}
                        </LayerGroup>
                    </LayersControl.Overlay>

                    {/* 7. Motor de Alertas Activas (ML) */}
                    <LayersControl.Overlay checked name="🚨 Alertas Activas (ML)">
                        <LayerGroup>
                            {alerts.map((alert, idx) => {
                                const isCritical = alert.severity === 'CRITICAL';
                                const color = isCritical ? '#d32f2f' : '#f57c00';

                                const relatedCase = alert.caso ? casos.find(c => c.id === alert.caso) : null;
                                const position = relatedCase ? [relatedCase.lat, relatedCase.lon] :
                                    municipalAggregates.find(m => m.municipio === alert.location_municipio)?.lat ?
                                        [municipalAggregates.find(m => m.municipio === alert.location_municipio).lat,
                                        municipalAggregates.find(m => m.municipio === alert.location_municipio).lon] : null;

                                if (!position) return null;

                                return (
                                    <CircleMarker
                                        key={`alert-engine-${idx}`}
                                        center={position}
                                        radius={30 + (isCritical ? 10 : 0)}
                                        pathOptions={{
                                            color: color,
                                            fillColor: color,
                                            fillOpacity: 0.3,
                                            dashArray: '10, 10',
                                            weight: 3
                                        }}
                                    >
                                        <Tooltip sticky>
                                            <div className="text-center">
                                                <strong>{alert.title}</strong><br />
                                                {alert.location_municipio}
                                            </div>
                                        </Tooltip>
                                        <Popup>
                                            <div style={{ width: '260px' }}>
                                                <div className={`d-flex align-items-center mb-2 ${isCritical ? 'text-danger' : 'text-warning'}`}>
                                                    <i className={`bi ${isCritical ? 'bi-exclamation-triangle-fill' : 'bi-info-circle-fill'} me-2 fs-5`}></i>
                                                    <h6 className="fw-bold mb-0">{alert.title}</h6>
                                                </div>
                                                <p className="small mb-2 text-dark" style={{ lineHeight: '1.4' }}>{alert.message}</p>
                                                <div className="bg-light p-2 rounded small mb-2 border">
                                                    <strong>Territorio:</strong> {alert.location_municipio}<br />
                                                    <strong>Tipo:</strong> {alert.alert_type === 'EPIDEMIOLOGICAL' ? 'Epidemiológica' : 'Climática'}
                                                </div>
                                                <div className={`alert p-2 mb-0 ${isCritical ? 'alert-danger' : 'alert-warning'}`} style={{ fontSize: '0.75rem' }}>
                                                    <strong>Interpretación ML:</strong> {isCritical ? 'Acción inmediata - Riesgo Elevado.' : 'Seguimiento recomendado.'}
                                                </div>
                                            </div>
                                        </Popup>
                                    </CircleMarker>
                                );
                            })}
                        </LayerGroup>
                    </LayersControl.Overlay>
                </LayersControl>
            </MapContainer>

            {/* Indicador de Carga de Clima */}
            {
                loadingWeather && (
                    <div style={{
                        position: 'absolute', top: '10px', left: '50px', zIndex: 1000,
                        background: 'white', padding: '5px 15px', borderRadius: '20px',
                        boxShadow: '0 2px 5px rgba(0,0,0,0.2)', fontSize: '0.8rem',
                        display: 'flex', alignItems: 'center'
                    }}>
                        <div className="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
                        Actualizando clima...
                    </div>
                )
            }
        </div >
    );
};

export default MapComponent;
